/**
 * Event-related types including tickets, agenda, and event details
 */

import type { Timezone } from "@shared/types/commonTypes";
import type { ImageDetailType, MediaItem, MultilingualText, UUID } from "./metadata";

// Event status enum
export enum EventStatusType {
  UPCOMING = "upcoming",
  ONGOING = "ongoing",
  COMPLETED = "completed",
  CANCELLED = "cancelled"
}

export enum EventBookType {
  PAY = "pay",
  REDIRECT = "redirect"
}

// Event ticket interface
export interface EventTicket {
  id: string;
  visibility: number;
  index: number;
  currency: string;
  price: number;
  minQuantity: number;
  status: "public" | "unlisted" | "private" | "expired" | "archived";
  title: MultilingualText;
  description?: MultilingualText;
  expiresAt?: string;
  startsAt?: string;
}

// Ticket form data for creating/editing tickets
export interface TicketFormData {
  title: MultilingualText;
  description?: MultilingualText;
  price: number;
  minQuantity: number;
  isFree: boolean;
  hasExpiry: boolean;
  expiresAt?: string;
  hasStart: boolean;
  startsAt?: string;
  visibility: number;
}

export interface GallerySection {
  title: MultilingualText;
  description?: MultilingualText;
  media: MediaItem[];
}

// FAQ-related interfaces
export interface FAQItem {
  question: MultilingualText;
  answer: MultilingualText;
}

export interface Testimonials {
  title: MultilingualText;
  description: MultilingualText;
  testimonials: TestimonialItem[];
}

// Testimonials-related interfaces
export interface TestimonialItem {
  date: string
  name: MultilingualText;
  testimonial: MultilingualText;
  video?: string;
  image?: ImageDetailType[];
  singleLanguage?: boolean;
}

// Related events interface
export interface EventRelation {
  id: string;
  relation: string;
}

// Agenda-related interfaces
export interface SessionTag {
  label: MultilingualText;
  color: string;
}

export interface AgendaSession {
  multiLine: boolean;
  from: string;
  to: string;
  title: MultilingualText;
  description?: MultilingualText;
  instructorIds?: UUID[];
  tags?: SessionTag[];
  allowedByTicketIds?: UUID[];
}

export interface AgendaSwimlane {
  title: MultilingualText;
  description?: MultilingualText;
  sessions: AgendaSession[];
}

export interface AgendaDay {
  date: string;
  swimlines: AgendaSwimlane[];
}

// Detailed event information (used in EventDetails)
export interface Event {
  id: string;
  isMy: boolean;
  publicURL: string;
  publicTitle: string;
  status: EventStatusType;
  visibility: number;
  verified: boolean;
  featured: boolean;
  slug: string;
  title: MultilingualText;
  description: MultilingualText;
  formatId: UUID;
  dateStart: string;
  dateEnd: string;
  languageCodes: string[];
  book: EventBookType;
  spotsMax: number;
  spotsCurrent: number;
  spotsLeft: number;
  spotsOverride?: number | null;
  tickets: EventTicket[];
  priceCurrency: string;
  categoryIds: UUID[];
  organizerIds: UUID[];
  instructorIds: UUID[];
  brandIds: UUID[];
  venueIds: UUID[];
  timezone: Timezone;
  expertiseLevelIds: UUID[];
  views: number;
  gallery?: GallerySection;
  whyAttend?: GallerySection;
  faq?: FAQItem[];
  testimonials?: Testimonials;
  agenda?: AgendaDay[];
  relatedEvents?: EventRelation[];
  // Optional fields for editing
  privateNote?: string;
  privateNoteTone?: string;
  enableOnlinePayments?: boolean;
  sourceUrl?: string | null;
  bookRedirectLink?: string | null;
  bookRedirectLinkClicks: number;
}

// Simplified event type for list/dropdown usage
export interface EventListItem {
  id: string;
  name: string;
  publicTitle: string;
  status: EventStatusType;
  dateStart: string;
  dateEnd?: string;
  amount?: number;
  currency?: string;
}
